import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CollapsibleReasoning } from './components/collapsible-reasoning';
import { CompactingMessage } from './components/compacting-message';
import { KimErrorState } from './components/kim-error-state';
import { KimMessage } from './components/kim-message';
import { ReasoningTempWindow } from './components/reasoning-temp-window';
import { ReferenceSidePanel } from './components/reference-side-panel';
import { ScrollToBottomButton } from './components/scroll-to-bottom-button';
import { VerifyDisclaimer } from './components/verify-disclaimer';
import { KimThreadStatus } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import {
  ContentBlock,
  ArtifactBlock,
  DocumentDownloadOptions,
  KimMessageType,
  ReferenceType,
  StreamBlock,
  StreamKimMessageType,
} from '../../../../../../../../../../backend/src/types/types';
import { ThreadDownloadDialog } from '../../kim/:threadId/components/answer/thread-download-dialog';
import { KimInput } from '../components/kim-input';
import { buildExportContent } from '../utils/build-export-content';
import { BaseExportEvent } from '@/api/hooks/useExportEvents';
import { KimStreamChunk, useKimStream } from '@/api/hooks/useKimStream';
import { useExportKimToWord } from '@/api/mutations/useExportKimToWord';
import { useExportKimWithDocuments } from '@/api/mutations/useExportKimWithDocuments';
import { useSendKimMessage } from '@/api/mutations/useSendKimMessage';
import { useGetKimMessages } from '@/api/queries/useGetKimMessages';
import { useGetKimThread } from '@/api/queries/useGetKimThread';
import { useUserContext } from '@/Contexts/User';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';
import { Icon } from '@/new/components/icon';

/** Merge artifact into content: if last block is same id and version, append content; otherwise push. */
function mergeArtifactIntoContent(content: ContentBlock[], artifact: ArtifactBlock): void {
  const last = content[content.length - 1];
  if (last?.type === 'artifact' && last.id === artifact.id && last.versionNumber === artifact.versionNumber) {
    const merged: ArtifactBlock = {
      type: 'artifact',
      id: artifact.id,
      name: artifact.name ?? last.name,
      content: [...(last as ArtifactBlock).content, ...artifact.content],
      versionNumber: artifact.versionNumber,
    };
    content[content.length - 1] = merged;
    return;
  }
  content.push(artifact);
}

export function NewKimThread() {
  const { matterId, subsetId, threadId } = useParams();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const trackingElementRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);

  const { data: thread } = useGetKimThread(matterId, subsetId, threadId);
  const { user } = useUserContext();
  const threadName = thread?.name;

  const {
    data: messages,
    isLoading: messagesLoading,
    isSuccess: messagesLoaded,
  } = useGetKimMessages(matterId, subsetId, threadId);

  const initialLastMessageId = useMemo(() => {
    if (!messagesLoaded || !messages?.length) return undefined;

    const lastMessage = messages[messages.length - 1];
    return String(new Date(lastMessage.createdDate).getTime());
  }, [messages, messagesLoaded]);

  const { chunks } = useKimStream({
    matterId: matterId!,
    subsetId: subsetId!,
    threadId: threadId!,
    enabled: Boolean(matterId && subsetId && threadId && messagesLoaded),
    initialLastMessageId,
  });

  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;

    const observer = new ResizeObserver(() => {
      const container = scrollContainerRef.current;
      const tracking = trackingElementRef.current;
      if (!container || !tracking) return;

      const userMessages = container.querySelectorAll('[data-user-message]');
      const lastUserMessage = userMessages[userMessages.length - 1];
      if (!lastUserMessage) return;

      const containerRect = container.getBoundingClientRect();
      const trackingRect = tracking.getBoundingClientRect();
      const lastUserMsgRect = lastUserMessage.getBoundingClientRect();

      // Convert to scroll-independent positions relative to container top
      const scrollTop = container.scrollTop;
      const trackingTop = trackingRect.top - containerRect.top + scrollTop;
      const lastUserMsgTop = lastUserMsgRect.top - containerRect.top + scrollTop;
      const distance = trackingTop - lastUserMsgTop;

      if (spacerRef.current) {
        spacerRef.current.style.height = `${Math.max(96, container.clientHeight - distance)}px`;
      }

      checkScrollToBottom();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const checkScrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollToBottom(distanceFromBottom > 200);
  }, []);

  const [sidePanel, setSidePanel] = useState<{
    isOpen: boolean;
    activeId: string | null;
    activeType: ReferenceType | null;
  }>({ isOpen: false, activeId: null, activeType: null });

  const openSidePanel = useCallback((id: string, referenceType: ReferenceType) => {
    setSidePanel({ isOpen: true, activeId: id, activeType: referenceType });
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanel({ isOpen: false, activeId: null, activeType: null });
  }, []);

  // Reset scroll and close side panel when switching threads
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
    closeSidePanel();
  }, [threadId]);

  const spacerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const { mutate: sendMessage, isLoading: isSending } = useSendKimMessage(matterId, subsetId, threadId);

  // Download state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadMessageId, setDownloadMessageId] = useState<string | undefined>(undefined);
  const [downloadArtifactId, setDownloadArtifactId] = useState<string | undefined>(undefined);

  const { mutate: exportToWord } = useExportKimToWord(matterId, subsetId);
  const { startExport, downloadExport, getProgressEndpoint } = useExportKimWithDocuments(
    matterId,
    subsetId,
    threadId,
    downloadMessageId,
  );

  const handleDownloadClick = useCallback((messageId: string, artifactId?: string) => {
    setDownloadMessageId(messageId);
    setDownloadArtifactId(artifactId);
    setShowDownloadModal(true);
  }, []);

  const handleExportDownload = useCallback(async () => {
    if (!currentExportId) return;
    try {
      await downloadExport(currentExportId);
      toast.success('Download started');
      setShowExportProgressModal(false);
      setIsCreatingExport(false);
      setCurrentExportId(null);
    } catch {
      toast.error('Download failed. Please try again.');
    }
  }, [currentExportId, downloadExport]);

  const isContentBlock = (block: StreamBlock): block is ContentBlock => block.type !== 'end';

  const { artifactsById, latestArtifactId } = useMemo(() => {
    const map = new Map<string, ArtifactBlock>();

    // Collect from persisted messages
    messages?.forEach((msg) => {
      msg.content.forEach((block) => {
        if (block.type === 'artifact') {
          const a = block as ArtifactBlock;
          const artifactId = a.id + '-' + a.versionNumber;
          map.set(artifactId, a);
        }
      });
    });

    // Merge streamed artifact chunks
    chunks.forEach((chunk) => {
      if (!isContentBlock(chunk.content) || chunk.content.type !== 'artifact') return;
      const artifact = chunk.content as ArtifactBlock;
      const artifactId = artifact.id + '-' + artifact.versionNumber;
      const existing = map.get(artifactId);
      if (existing) {
        map.set(artifactId, {
          type: 'artifact',
          id: artifact.id,
          name: artifact.name ?? existing.name,
          content: [...existing.content, ...artifact.content],
          versionNumber: existing.versionNumber ?? artifact.versionNumber,
        });
      } else {
        map.set(artifactId, { ...artifact, content: [...artifact.content] });
      }
    });

    // Latest artifact is the last key in insertion order
    const keys = [...map.keys()];
    return { artifactsById: map, latestArtifactId: keys.length ? keys[keys.length - 1] : null };
  }, [messages, chunks]);

  // Same as below - likely will need to rework
  const referenceIndexMap = useMemo(() => {
    const map = new Map<string, { index: number; referenceType: ReferenceType }>();
    let index = 1;
    const trackBlock = (block: ContentBlock) => {
      if (block.type === 'reference' && !map.has(block.id)) {
        map.set(block.id, { index: index++, referenceType: block.refType });
      }
      if (block.type === 'artifact') {
        block.content.forEach(trackBlock);
      }
    };
    messages?.forEach((msg) => msg.content.forEach(trackBlock));
    chunks.forEach((chunk) => {
      if (isContentBlock(chunk.content)) trackBlock(chunk.content);
    });
    return map;
  }, [messages, chunks]);

  const handleDownload = useCallback(
    async (options: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
      setShowDownloadModal(false);

      const message = (messages ?? []).find((m) => String(m.id) === downloadMessageId);
      if (!message) return;

      let contentToExport = message.content;
      let title = threadName || 'Kim Draft';
      const isArtifact = !!downloadArtifactId;

      if (isArtifact) {
        const artifact = artifactsById.get(downloadArtifactId);
        if (artifact) {
          contentToExport = artifact.content;
          if (artifact.name) title = artifact.name;
        }
      }

      const { markdown, references } = buildExportContent(contentToExport, referenceIndexMap);
      const exportOptions = isArtifact ? { ...options, includeTitle: false } : options;

      // If including source documents call request which goes to worker
      if (includeSourceDocuments) {
        if (currentExportId) {
          setShowExportProgressModal(true);
          return;
        }
        try {
          setExportError(undefined);
          const result = await startExport(title, markdown, references, exportOptions);
          if (result?.exportId) setCurrentExportId(result.exportId);
          setIsCreatingExport(true);
          setShowExportProgressModal(true);
        } catch (error) {
          setExportError(error instanceof Error ? error.message : 'Failed to start export');
          setShowExportProgressModal(true);
        }
      } else {
        setDownloadBusy(true);
        exportToWord(
          { title, content: markdown, references, options: exportOptions },
          { onSettled: () => setDownloadBusy(false) },
        );
      }
    },
    [
      currentExportId,
      downloadMessageId,
      downloadArtifactId,
      artifactsById,
      messages,
      referenceIndexMap,
      threadName,
      startExport,
      exportToWord,
    ],
  );

  const collectedReferences = useMemo(
    () =>
      Array.from(referenceIndexMap.entries()).map(([id, { index, referenceType }]) => ({ id, index, referenceType })),
    [referenceIndexMap],
  );

  // Group consecutive reasoning-temp chunks into separate windows.
  // A new window starts whenever a non-reasoning-temp chunk appears between reasoning-temp chunks.
  const tempReasoningGroups = useMemo(() => {
    const groups: { chunks: KimStreamChunk[]; items: string[]; timestamp: number }[] = [];
    let sawNonTemp = false;

    for (const chunk of chunks) {
      if (chunk.type === StreamKimMessageType.AiReasoningTemp) {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || sawNonTemp) {
          // Start a new group
          groups.push({ chunks: [chunk], items: [], timestamp: chunk.timestamp });
          sawNonTemp = false;
        } else {
          lastGroup.chunks.push(chunk);
        }
        // Extract text items
        if (chunk.content.type === 'text') {
          groups[groups.length - 1].items.push((chunk.content as { type: 'text'; value: string }).value);
        }
      } else {
        sawNonTemp = true;
      }
    }

    return groups;
  }, [chunks]);

  // This currently groups streamed chunks every time a new chunk comes through - I don't think this
  // will work long-term. I think we'll need a different approach with even types like message
  // start/add to message/message end to more efficiently group chunks.
  const groupedChunks = useMemo(() => {
    const groups: { type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      // Skip temp reasoning and compacting chunks — they're rendered separately
      if (chunk.type === StreamKimMessageType.AiReasoningTemp) continue;
      if (chunk.type === KimMessageType.Compacting) continue;

      if (chunk.content.type === 'start') continue;

      if (chunk.content.type === 'end') {
        const last = groups[groups.length - 1];
        if (last) last.isComplete = true;
        continue;
      }

      const last = groups[groups.length - 1];

      if (last && last.type === chunk.type && chunk.type !== KimMessageType.AiReasoning) {
        if (chunk.content.type === 'artifact') {
          mergeArtifactIntoContent(last.content, chunk.content);
        } else {
          last.content.push(chunk.content);
        }
      } else {
        groups.push({
          type: chunk.type as KimMessageType,
          content: [chunk.content],
          isComplete: false,
          timestamp: chunk.timestamp,
        });
      }
    }
    return groups;
  }, [chunks]);

  // Track compacting events from the stream
  const compactingEvents = useMemo(() => {
    const events: { isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      if (chunk.type !== KimMessageType.Compacting) continue;
      if (chunk.content.type === 'start') {
        events.push({ isComplete: false, timestamp: chunk.timestamp });
      } else if (chunk.content.type === 'end') {
        const last = events[events.length - 1];
        if (last) last.isComplete = true;
      }
    }
    return events;
  }, [chunks]);

  // Build a unified render list so each reasoning temp window stays at its timestamp position
  const streamItems = useMemo(() => {
    const items: (
      | { kind: 'message'; type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }
      | { kind: 'reasoning-temp'; groupIndex: number; items: string[]; timestamp: number }
      | { kind: 'compacting'; isComplete: boolean; timestamp: number }
    )[] = [];

    for (const group of groupedChunks) {
      items.push({ kind: 'message', ...group });
    }

    tempReasoningGroups.forEach((group, idx) => {
      items.push({
        kind: 'reasoning-temp',
        groupIndex: idx,
        items: group.items,
        timestamp: group.timestamp,
      });
    });

    for (const event of compactingEvents) {
      items.push({ kind: 'compacting', ...event });
    }

    items.sort((a, b) => a.timestamp - b.timestamp);
    return items;
  }, [groupedChunks, tempReasoningGroups, compactingEvents]);

  // True when the agent hasn't finished responding to the user's last message
  const isAgentBusy = useMemo(() => {
    // If there's an AI stream message, check if it's still streaming
    const lastAiStream = streamItems.findLast((item) => item.kind === 'message' && item.type === KimMessageType.Ai);
    if (lastAiStream?.kind === 'message') return !lastAiStream.isComplete;

    // No AI stream content — check if DB has an AI response after the last user message
    if (!messagesLoaded || !messages?.length) return false;
    const lastUserIdx = messages.findLastIndex((m) => m.type === KimMessageType.User);
    if (lastUserIdx === -1) return false;
    return !messages.slice(lastUserIdx + 1).some((m) => m.type === KimMessageType.Ai);
  }, [messagesLoaded, messages, streamItems]);

  // Unified display list: combines DB messages and stream items, grouping consecutive reasoning
  const displayItems = useMemo(() => {
    type LeafItem =
      | {
          kind: 'db-message';
          key: string;
          msgId: string;
          type: KimMessageType;
          content: ContentBlock[];
          createdDate: string;
        }
      | {
          kind: 'stream-message';
          key: string;
          streamIndex: number;
          type: KimMessageType;
          content: ContentBlock[];
          isComplete: boolean;
          timestamp: number;
        }
      | { kind: 'reasoning-temp'; key: string; items: string[] }
      | { kind: 'db-compacting'; key: string }
      | { kind: 'stream-compacting'; key: string; isComplete: boolean };

    type DisplayItem =
      | { kind: 'reasoning-group'; key: string; isFollowedByAi: boolean; children: LeafItem[] }
      | LeafItem;

    const flat: Array<{ isReasoning: boolean; isAi: boolean; leaf: LeafItem }> = [];

    for (const msg of messages ?? []) {
      if (msg.type === KimMessageType.Compacting) {
        flat.push({
          isReasoning: false,
          isAi: false,
          leaf: { kind: 'db-compacting', key: `db-${msg.id}` },
        });
      } else {
        flat.push({
          isReasoning: msg.type === KimMessageType.AiReasoning,
          isAi: msg.type === KimMessageType.Ai,
          leaf: {
            kind: 'db-message',
            key: `db-${msg.id}`,
            msgId: String(msg.id),
            type: msg.type,
            content: msg.content,
            createdDate: String(msg.createdDate),
          },
        });
      }
    }

    for (const [i, si] of streamItems.entries()) {
      if (si.kind === 'reasoning-temp') {
        flat.push({
          isReasoning: true,
          isAi: false,
          leaf: {
            kind: 'reasoning-temp',
            key: `reasoning-temp-${si.groupIndex}`,
            items: si.items,
          },
        });
      } else if (si.kind === 'compacting') {
        flat.push({
          isReasoning: false,
          isAi: false,
          leaf: { kind: 'stream-compacting', key: `compacting-${i}`, isComplete: si.isComplete },
        });
      } else {
        flat.push({
          isReasoning: si.type === KimMessageType.AiReasoning,
          isAi: si.type === KimMessageType.Ai,
          leaf: {
            kind: 'stream-message',
            key: `stream-${i}`,
            streamIndex: i,
            type: si.type,
            content: si.content,
            isComplete: si.isComplete,
            timestamp: si.timestamp,
          },
        });
      }
    }

    const result: DisplayItem[] = [];
    let buffer: LeafItem[] = [];
    let groupCounter = 0;

    const flush = (isFollowedByAi: boolean) => {
      if (buffer.length > 0) {
        result.push({
          kind: 'reasoning-group',
          key: `rg-${groupCounter++}`,
          isFollowedByAi,
          children: [...buffer],
        });
        buffer = [];
      }
    };

    for (const { isReasoning, isAi, leaf } of flat) {
      if (isReasoning) {
        buffer.push(leaf);
      } else {
        flush(isAi);
        result.push(leaf);
      }
    }
    flush(false);

    return result;
  }, [messages, streamItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    const lastItem = displayItems[displayItems.length - 1];
    const userJustSentMessage = lastItem?.kind === 'db-message' && lastItem.type === KimMessageType.User;

    if (isAtBottom || userJustSentMessage) {
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [displayItems, chunks.length]);

  const renderDisplayLeaf = (item: (typeof displayItems)[number]) => {
    switch (item.kind) {
      case 'db-message':
        return (
          <KimMessage
            key={item.key}
            type={item.type}
            content={item.content}
            isComplete
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={openSidePanel}
            onDownloadClick={handleDownloadClick}
            messageId={item.msgId}
            artifactsById={artifactsById}
            latestArtifactId={latestArtifactId}
            isDownloading={downloadBusy}
          />
        );
      case 'stream-message':
        return (
          <KimMessage
            key={item.key}
            type={item.type}
            content={item.content}
            isComplete={item.isComplete}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={openSidePanel}
            onDownloadClick={handleDownloadClick}
            artifactsById={artifactsById}
            latestArtifactId={latestArtifactId}
            isDownloading={downloadBusy}
          />
        );
      case 'reasoning-temp':
        return <ReasoningTempWindow key={item.key} items={item.items} />;
      case 'db-compacting':
        return <CompactingMessage key={item.key} isComplete />;
      case 'stream-compacting':
        return <CompactingMessage key={item.key} isComplete={item.isComplete} />;
      default:
        return null;
    }
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || isSending) return;

    sendMessage(text, {
      onSuccess: () => setInputValue(''),
    });
  };

  return (
    <div className="relative flex h-full w-full">
      {thread && user && thread.createdById !== user.id && (
        <div className="absolute top-2 left-0 flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs text-gray-500">
          <Icon className="lucide--user-2" />
          Thread created by <strong className="font-medium text-gray-700">{thread?.createdBy.email}</strong>
        </div>
      )}

      {/* Message column */}
      <div className="mr-2 flex min-w-0 grow flex-col pb-0">
        <div className="flex w-full grow flex-col justify-between overflow-hidden">
          <div
            ref={scrollContainerRef}
            onScroll={checkScrollToBottom}
            className="min-h-0 grow space-y-4 overflow-y-auto font-serif"
          >
            {messagesLoading && <div className="my-8 text-center text-sm text-gray-400">Loading messages...</div>}

            <div ref={scrollContentRef} className="mx-auto max-w-3xl px-4 pt-24">
              {displayItems.map((item) =>
                item.kind === 'reasoning-group' ? (
                  <CollapsibleReasoning key={item.key} isComplete={item.isFollowedByAi}>
                    {item.children.map((child) => renderDisplayLeaf(child))}
                  </CollapsibleReasoning>
                ) : (
                  renderDisplayLeaf(item)
                ),
              )}

              {isAgentBusy &&
                thread?.status !== KimThreadStatus.Failed &&
                !streamItems.some((item) => item.kind === 'message' && item.type === KimMessageType.Ai) && (
                  <div className="my-4">
                    <Icon className="lucide--loader animate-spin text-base text-gray-400 ease-in-out" />
                  </div>
                )}
            </div>

            <div ref={trackingElementRef} />
            <div ref={spacerRef} />
          </div>
          <AnimatePresence initial={false}>
            <div className="relative -m-1 mx-auto mb-1 w-full max-w-3xl space-y-2 p-1">
              <div className="absolute bottom-full flex w-full flex-col items-center gap-3 pr-2">
                <ScrollToBottomButton
                  visible={showScrollToBottom}
                  onClick={() =>
                    scrollContainerRef.current?.scrollTo({
                      top: scrollContainerRef.current.scrollHeight,
                      behavior: 'smooth',
                    })
                  }
                />

                {thread?.status === KimThreadStatus.Failed && (
                  <motion.div
                    key="error-state"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="w-full"
                  >
                    <KimErrorState />
                  </motion.div>
                )}

                {thread && user && thread.createdById !== user.id && (
                  <motion.div
                    key="cannot-message"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
                  >
                    <Icon className="lucide--info mt-0.75" />
                    <span>You can't send messages to a thread created by another user.</span>
                  </motion.div>
                )}
              </div>

              {!(thread && user && thread.createdById !== user.id) && (
                <motion.div
                  key="kim-input"
                  initial={{ opacity: 0, filter: 'blur(2px)' }}
                  animate={{ opacity: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, filter: 'blur(2px)' }}
                  transition={{ duration: 0.15 }}
                >
                  <KimInput
                    className="max-h-100 min-h-24 rounded-lg shadow-lg/3"
                    placeholder="Ask Kim..."
                    value={inputValue}
                    onChange={setInputValue}
                    onSubmit={handleSendMessage}
                    disabled={
                      !messages || !thread || isSending || isAgentBusy || thread?.status === KimThreadStatus.Failed
                    }
                  />
                </motion.div>
              )}

              <VerifyDisclaimer />
            </div>
          </AnimatePresence>
        </div>
      </div>

      {/* Download dialogs */}
      <ThreadDownloadDialog
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        isLoading={downloadBusy}
      />

      {downloadMessageId && (
        <GenericExportProgressModal<BaseExportEvent>
          isOpen={showExportProgressModal}
          onClose={() => {
            setShowExportProgressModal(false);
            setIsCreatingExport(false);
          }}
          exportConfig={{
            progressEndpoint: getProgressEndpoint(downloadMessageId),
            exportId: currentExportId,
          }}
          itemName="Kim Answer"
          onDownload={handleExportDownload}
          isCreating={isCreatingExport}
          error={exportError}
        />
      )}

      {/* Reference side panel - conditionally rendered */}
      <AnimatePresence>
        {sidePanel.isOpen && sidePanel.activeId && (
          <ReferenceSidePanel
            onClose={closeSidePanel}
            references={collectedReferences}
            activeReferenceId={sidePanel.activeId}
            onSelectReference={openSidePanel}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
